import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  // 419 (stale CSRF token) must REJECT so the response interceptor below can
  // reload the page and pick up a fresh token — everything else under 500
  // resolves and is handled by the caller via the {success, message} envelope.
  validateStatus: (status) => status >= 200 && status < 500 && status !== 419,
});

// EncryptCookies middleware is not registered in this app's web group, so the
// XSRF-TOKEN cookie cannot be used (Laravel would try to Crypt::decrypt the
// X-XSRF-TOKEN header and fail). Send the raw session token as X-CSRF-TOKEN
// instead — Laravel accepts it as plaintext.
// The token is maintained on window.__csrfToken by lib/csrf-init.ts, which
// hydrates it from Inertia's initial page props and refreshes it after every
// navigation.
axiosInstance.interceptors.request.use((config) => {
  const token = window.__csrfToken;
  if (token) {
    config.headers.set('X-CSRF-TOKEN', token);
  }
  return config;
});

// Reload on 419 so the next request picks up the fresh token
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 419) {
      window.location.reload();
    }
    return Promise.reject(error);
  },
);

// Type for API response
export type ApiResponse<T = any> = {
  data: T;
  message?: string;
  status: number;
  success: boolean;
};

// Type for API error
export type ApiError = {
  message: string;
  errors?: Record<string, string[]>;
  status: number;
};

/**
 * Base GET request
 * @param url - The endpoint URL
 * @param config - Optional axios config
 */
export const get = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.get(url, config);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Base POST request
 * @param url - The endpoint URL
 * @param data - The data to send
 * @param config - Optional axios config
 */
export const post = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.post(url, data, config);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Base PUT request
 * @param url - The endpoint URL
 * @param data - The data to send
 * @param config - Optional axios config
 */
export const put = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.put(url, data, config);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Base PATCH request
 * @param url - The endpoint URL
 * @param data - The data to send
 * @param config - Optional axios config
 */
export const patch = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.patch(url, data, config);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Base DELETE request
 * @param url - The endpoint URL
 * @param config - Optional axios config
 */
export const del = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.delete(url, config);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Coerce an unknown API `message` into a string safe for toast/JSX.
 * Some error paths (e.g. exception renderers) have historically returned
 * `message` as an object — rendering that as a React child crashes the page.
 */
export const toErrorMessage = (message: unknown, fallback: string): string => {
  if (typeof message === 'string' && message) return message;
  if (message && typeof message === 'object') {
    const first = Object.values(message as Record<string, unknown>).find((v): v is string => typeof v === 'string' && v !== '');
    if (first) return first;
  }
  return fallback;
};

/**
 * Handle axios errors and transform them into a consistent format
 * @param error - The error to handle
 */
const handleError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    return {
      message: axiosError.response?.data?.message || 'An error occurred',
      errors: axiosError.response?.data?.errors,
      status: axiosError.response?.status || 500,
    };
  }

  return {
    message: 'An unexpected error occurred',
    status: 500,
  };
};
